import React, { useRef, useEffect, useState, useCallback } from "react";
import { useLoadScript } from "@react-google-maps/api";

// Australian states for the dropdown
const AU_STATES = [
  { value: "ACT", label: "Australian Capital Territory" },
  { value: "NSW", label: "New South Wales" },
  { value: "NT", label: "Northern Territory" },
  { value: "QLD", label: "Queensland" },
  { value: "SA", label: "South Australia" },
  { value: "TAS", label: "Tasmania" },
  { value: "VIC", label: "Victoria" },
  { value: "WA", label: "Western Australia" },
];

const GOOGLE_MAPS_LIBRARIES = ["places"];

/**
 * AddressForm — reusable structured address form with Google Places autocomplete.
 *
 * Props:
 *   prefix        {string}   — field-name prefix, e.g. "billing" or "shipping"
 *   title         {string}   — section heading, e.g. "Billing Address"
 *   values        {object}   — current address field values
 *   errors        {object}   — validation error messages keyed by field name
 *   onChange      {function} — (fieldName, value) => void
 */
export const AddressForm = ({ prefix = "billing", title, values = {}, errors = {}, onChange }) => {
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const [finderValue, setFinderValue] = useState("");

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // field helpers — build prefixed key names
  const f = (name) => `${prefix}_${name}`;

  // Initialise Google Places autocomplete on the finder input
  useEffect(() => {
    if (!isLoaded || !inputRef.current) return;
    if (!window.google || !window.google.maps || !window.google.maps.places) return;

    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: "au" },
      fields: ["address_components"],
      types: ["address"],
    });

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.address_components) return;

      let streetNumber = "";
      let route = "";
      let suburb = "";
      let state = "";
      let postcode = "";

      place.address_components.forEach((component) => {
        const types = component.types;
        if (types.includes("street_number")) streetNumber = component.long_name;
        if (types.includes("route")) route = component.long_name;
        if (types.includes("locality")) suburb = component.long_name;
        if (types.includes("administrative_area_level_1")) state = component.short_name;
        if (types.includes("postal_code")) postcode = component.long_name;
      });

      const streetAddress = streetNumber ? `${streetNumber} ${route}`.trim() : route;

      onChange(f("StreetAddress"), streetAddress);
      onChange(f("Suburb"), suburb);
      onChange(f("State"), state);
      onChange(f("Postcode"), postcode);

      // Update finder display value
      setFinderValue(streetAddress);
    });

    autocompleteRef.current = autocomplete;
  }, [isLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldChange = useCallback(
    (e) => {
      onChange(e.target.name, e.target.value);
    },
    [onChange]
  );

  return (
    <div className="address-form-section">
      {title && <h3 className="address-form-section__title">{title}</h3>}

      {/* ── Address Finder / Autocomplete ─────────────────────────────── */}
      {/* <div className="checkout_form_tel address-finder">
        <h3>Address Finder</h3>
        <input
          ref={inputRef}
          type="text"
          placeholder="Start typing your address…"
          value={finderValue}
          onChange={(e) => setFinderValue(e.target.value)}
          autoComplete="off"
          disabled={!isLoaded}
          className="address-finder__input"
        />
        {!isLoaded && (
          <p className="address-finder__hint">Address search loading…</p>
        )}
        {isLoaded && !process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
          <p className="address-finder__hint" style={{ color: "#e67e22" }}>
            Google Maps API key not configured — please fill fields manually.
          </p>
        )}
      </div> */}

      {/* ── Company (optional) ───────────────────────────────────────── */}
      <div className="checkout_form_tel">
        <h3>Company <span className="field-optional">(Optional)</span></h3>
        <input
          type="text"
          name={f("Company")}
          placeholder="Company name"
          value={values[f("Company")] || ""}
          onChange={handleFieldChange}
        />
      </div>

      {/* ── Building / Level / Suite (optional) ──────────────────────── */}
      <div className="checkout_form_tel">
        <h3>Building <span className="field-optional">(Level / Suite / Shop — Optional)</span></h3>
        <input
          type="text"
          name={f("Building")}
          placeholder="e.g. Level 3, Suite 12"
          value={values[f("Building")] || ""}
          onChange={handleFieldChange}
        />
      </div>

      {/* ── Street Address (required) ────────────────────────────────── */}
      <div className="checkout_form_tel">
        <h3 className="required">Street Address</h3>
        <input
          type="text"
          name={f("StreetAddress")}
          placeholder="Street address"
          value={values[f("StreetAddress")] || ""}
          onChange={handleFieldChange}
        />
        {errors[f("StreetAddress")] && (
          <p className="giv_error">{errors[f("StreetAddress")]}</p>
        )}
      </div>

      {/* ── Suburb + Postcode (required) ─────────────────────────────── */}
      <div className="checkout_form_tel d-flex address-form-row">
        <div className="address-form-col">
          <h3 className="required">Suburb</h3>
          <input
            type="text"
            name={f("Suburb")}
            placeholder="Suburb"
            value={values[f("Suburb")] || ""}
            onChange={handleFieldChange}
          />
          {errors[f("Suburb")] && (
            <p className="giv_error">{errors[f("Suburb")]}</p>
          )}
        </div>
        <div className="address-form-col">
          <h3 className="required">Postcode</h3>
          <input
            type="text"
            name={f("Postcode")}
            placeholder="Postcode"
            value={values[f("Postcode")] || ""}
            onChange={handleFieldChange}
            maxLength={4}
          />
          {errors[f("Postcode")] && (
            <p className="giv_error">{errors[f("Postcode")]}</p>
          )}
        </div>
      </div>

      {/* ── State (required, dropdown) ───────────────────────────────── */}
      <div className="checkout_form_tel">
        <h3 className="required">State</h3>
        <select
          name={f("State")}
          value={values[f("State")] || ""}
          onChange={handleFieldChange}
        >
          <option value="">Select State</option>
          {AU_STATES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        {errors[f("State")] && (
          <p className="giv_error">{errors[f("State")]}</p>
        )}
      </div>
    </div>
  );
};

/**
 * Validate an address block.
 * Returns an errors object; empty object = valid.
 */
export function validateAddress(values, prefix) {
  const errors = {};
  const f = (name) => `${prefix}_${name}`;

  if (!values[f("StreetAddress")]?.trim()) {
    errors[f("StreetAddress")] = "Street Address is required";
  }
  if (!values[f("Suburb")]?.trim()) {
    errors[f("Suburb")] = "Suburb is required";
  }
  if (!values[f("Postcode")]?.trim()) {
    errors[f("Postcode")] = "Postcode is required";
  } else if (!/^\d{4}$/.test(values[f("Postcode")].trim())) {
    errors[f("Postcode")] = "Postcode must be 4 digits";
  }
  if (!values[f("State")]?.trim()) {
    errors[f("State")] = "State is required";
  }

  return errors;
}

/**
 * Copy billing address fields into shipping address fields.
 * Returns the merged values object.
 */
export function mirrorBillingToShipping(values) {
  return {
    ...values,
    shipping_Company: values.billing_Company || "",
    shipping_Building: values.billing_Building || "",
    shipping_StreetAddress: values.billing_StreetAddress || "",
    shipping_Suburb: values.billing_Suburb || "",
    shipping_Postcode: values.billing_Postcode || "",
    shipping_State: values.billing_State || "",
  };
}
